// On-demand font loading. Nothing in the catalog is loaded until a font is actually selected,
// previewed in the picker, or used by a rendered/generated slide — loading all ~1,900 families
// eagerly would be megabytes and thousands of requests. Deduped per family across the session.
import { FONT_CATALOG, getFontById, getFontByFamily, type FontMeta } from './catalog'

const loaded = new Set<string>()

const googleHref = (family: string, weights: number[]) => {
  // Request only the weights the font actually ships — keeps the URL valid and avoids faux-bold.
  const wght = weights.length ? [...weights].sort((a, b) => a - b).join(';') : '400'
  return `https://fonts.googleapis.com/css2?family=${family.replace(/\s+/g, '+')}:wght@${wght}&display=swap`
}

const fontshareHref = (family: string, weights: number[]) => {
  const slug = family.toLowerCase().replace(/\s+/g, '-')
  const w = (weights.length ? weights : [400]).join(',')
  return `https://api.fontshare.com/v2/css?f[]=${slug}@${w}&display=swap`
}

/**
 * The stylesheet URL for a catalogue font — the same one `loadFont` injects, handed out instead.
 *
 * It exists because the ARTIFACT needs it. Everything else in this file loads a face into the HOST
 * document, which is exactly no use to a document running in a sandboxed iframe on an opaque origin: a
 * font loaded out here is not a font that artifact can set. So the preview's text toolbar takes the
 * href and does two things the host never has to — posts it into the live frame's own head, and writes
 * it into the artifact's stored source, which is what makes the choice survive a reload.
 *
 * Derived from the same two builders rather than a third copy, so a font picked and a font previewed
 * can never resolve to different URLs.
 */
export function fontHref(meta?: FontMeta | null): string | null {
  if (!meta) return null
  const rawFamily = meta.family.replace(/['"]/g, '')
  return meta.source === 'fontshare'
    ? fontshareHref(rawFamily, meta.weights)
    : googleHref(rawFamily, meta.weights)
}

function inject(href: string, key: string) {
  if (typeof document === 'undefined') return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  link.setAttribute('data-font', key)
  document.head.appendChild(link)
}

/** Load a catalog font by its FontMeta. Idempotent. No-op on the server. */
export function loadFont(meta?: FontMeta | null) {
  if (!meta || typeof document === 'undefined') return
  if (loaded.has(meta.id)) return
  loaded.add(meta.id)
  const rawFamily = meta.family.replace(/['"]/g, '')
  const href = meta.source === 'fontshare' ? fontshareHref(rawFamily, meta.weights) : googleHref(rawFamily, meta.weights)
  inject(href, meta.id)
}

export const loadFontById = (id?: string | null) => loadFont(getFontById(id))

/** Resolve a stored CSS font-family string to the catalog and load it. Falls back to a best-effort
 *  Google lookup for legacy/brand families that predate the catalog (mirrors ensureBrandFont). */
export function loadFontByFamily(family?: string | null) {
  if (!family) return
  const meta = getFontByFamily(family)
  if (meta) return loadFont(meta)
  const lead = family.split(',')[0].replace(/['"]/g, '').trim()
  // Direct callers (benchTextures, captureSlide, SlideElement) pass raw CSS values too, so the
  // non-catalog fallback gets the same plausibility gate as the HTML scan above.
  if (!lead || !plausibleFamilyName(lead)) return
  const key = `x-${lead.toLowerCase()}`
  if (loaded.has(key)) return
  loaded.add(key)
  inject(googleHref(lead, [400, 500, 600, 700]), key)
}

/** Eagerly load the small set of defaults (called once for the editor's common fonts). */
export function preloadFonts(ids: readonly string[]) {
  for (const id of ids) loadFontById(id)
}

/** A string that can actually NAME a family. Extraction feeds this raw CSS, and artifact/site CSS
 *  routinely says `font-family: var(--a-font-body)` or arrives HTML-escaped (`&quot;Inter&quot;` → the
 *  entity fragment `&quot`); turning those into catalog URLs made garbage font requests on every boot
 *  (measured 2026-08-18: 5 × ERR_BLOCKED_BY_ORB per load of the default canvas). Syntax characters
 *  disqualify a name outright; real families are letters/digits/spaces/hyphens/dots. */
const plausibleFamilyName = (lead: string): boolean =>
  !!lead && !lead.startsWith('--') && !/[&(){};:<>@\\/="+%$#]/.test(lead)

/** Distinct lead font families referenced in a chunk of slide HTML (first token of each font-family). */
function familiesInHtml(html: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const m of html.matchAll(/font-family:\s*([^;"')]+)/gi)) {
    const lead = m[1].split(',')[0].replace(/['"]/g, '').trim()
    const key = lead.toLowerCase()
    if (lead && plausibleFamilyName(lead) && !seen.has(key)) {
      seen.add(key)
      out.push(lead)
    }
  }
  return out
}

/** All families referenced anywhere in a chunk of generated/loaded slide HTML, so a freshly
 *  hydrated or AI-generated deck renders in its real typefaces instead of silently falling back. */
export function loadFontsInHtml(html?: string | null) {
  if (!html) return
  for (const fam of familiesInHtml(html)) loadFontByFamily(fam)
}

/** Inject AND await every font referenced in a chunk of HTML, so a synchronous measurement pass
 *  (the flow → bake step) sizes each text box in its REAL face instead of the fallback — otherwise a
 *  generated title in, say, a wide display font would overflow once the real font swaps in post-bake.
 *  Bounded by `timeoutMs` so generation never hangs on a slow/missing font. */
export async function ensureFontsForHtml(html?: string | null, timeoutMs = 2500): Promise<void> {
  if (!html || typeof document === 'undefined' || !document.fonts) return
  const families = familiesInHtml(html)
  if (!families.length) return
  for (const fam of families) loadFontByFamily(fam)
  // Give the injected stylesheets a tick to register their @font-face rules before forcing the load.
  await new Promise((r) => setTimeout(r, 50))
  const loads: Promise<unknown>[] = []
  for (const fam of families) {
    for (const weight of ['400', '700']) loads.push(document.fonts.load(`${weight} 16px "${fam}"`).catch(() => {}))
  }
  await Promise.race([Promise.allSettled(loads), new Promise((r) => setTimeout(r, timeoutMs))])
}

export { FONT_CATALOG }
